// Where this page's pins go: on their element, at their stored position, or in the side list.
import { markerPoint, resolveAnchor } from "./anchor";
import type { PageContext } from "./context";
import type { Pin } from "./api";
import { feedbackGridRect } from "./subject";

export interface Placed {
  pin: Pin;
  x: number;
  y: number;
  /** Placed by the stored position, not by finding the element. */
  approximate: boolean;
}

export interface Placement {
  placed: Placed[];
  /** Pins whose element isn't on screen: the side list. */
  unplaced: Pin[];
}

/** Same loan or LO in view (both none counts as the same). */
export function sameSubject(pin: Pin, ctx: PageContext): boolean {
  return (pin.loan_id ?? null) === ctx.loan_id && (pin.core_loan_id ?? null) === ctx.core_loan_id && (pin.lo_person_id ?? null) === ctx.lo_person_id;
}

/** No loan or LO in the URL: a shared page such as /pipeline, whose rows each carry their own loan. */
function sharedPage(ctx: PageContext): boolean {
  return !ctx.loan_id && !ctx.core_loan_id && !ctx.lo_person_id;
}

/** A row-scoped pin on a shared page sits only on an element inside that same loan's boundary. */
function insideItsLoan(pin: Pin, el: Element): boolean {
  const box = el.closest("[data-loan-id], [data-core-loan-id]");
  if (!box) return false;
  return pin.loan_id ? box.getAttribute("data-loan-id") === pin.loan_id : box.getAttribute("data-core-loan-id") === pin.core_loan_id;
}

export function placePins(pins: Pin[], ctx: PageContext, doc: Document, win: Window): Placement {
  const placed: Placed[] = [];
  const unplaced: Pin[] = [];
  for (const pin of pins) {
    const rowPin = sharedPage(ctx) && !pin.lo_person_id && !!(pin.loan_id || pin.core_loan_id);
    if (pin.route !== ctx.route || (!sameSubject(pin, ctx) && !rowPin)) {
      unplaced.push(pin);
      continue;
    }
    // The stored position is only trusted on the very same view (same query, similar width).
    const sameView = pin.query === ctx.query;
    const res = resolveAnchor(pin.anchor, doc, sameView ? { viewportWidth: pin.viewport_width, currentWidth: win.innerWidth } : {});
    if (res.kind === "element" && res.el.tagName === "CANVAS" && res.el.closest("[data-feedback-grid]") && pin.loan_id) {
      const r = feedbackGridRect(res.el, pin.loan_id);
      const p = pin.anchor.point ?? { x: 0.5, y: 0.5 };
      const x = r ? r.x + r.width * p.x : -1;
      const y = r ? r.y + r.height * p.y : -1;
      const visible = r?.visible ?? r;
      if (visible && x >= visible.x && y >= visible.y && x < visible.x + visible.width && y < visible.y + visible.height
        && x >= 0 && y >= 0 && x <= win.innerWidth && y <= win.innerHeight) {
        placed.push({ pin, x, y, approximate: false });
      } else unplaced.push(pin);
      continue;
    }
    // A row pin needs its own loan's element: a recycled or re-sorted row must never carry another loan's pin.
    if (rowPin && (res.kind !== "element" || !insideItsLoan(pin, res.el))) {
      unplaced.push(pin);
      continue;
    }
    const pt = markerPoint(res, pin.anchor, win);
    const onScreen = pt && pt.x >= -8 && pt.y >= -8 && pt.x <= win.innerWidth + 8 && pt.y <= win.innerHeight + 8;
    if (pt && onScreen) placed.push({ pin, x: pt.x, y: pt.y, approximate: res.kind === "position" });
    else unplaced.push(pin);
  }
  return { placed, unplaced };
}
